import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Gift, ArrowRight } from 'lucide-react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';

export const EmailPopup = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Check if user has already seen/dismissed the popup
    const hasSeenPopup = localStorage.getItem('aphrodite_popup_seen');
    
    if (!hasSeenPopup) {
      // Show popup after 5 seconds
      const timer = setTimeout(() => {
        setIsOpen(true);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, []);

  const handleClose = () => {
    setIsOpen(false);
    localStorage.setItem('aphrodite_popup_seen', 'true');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setIsLoading(true);
    
    // Simulate API call - in production, connect to email service
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    setIsSubmitted(true);
    setIsLoading(false);
    localStorage.setItem('aphrodite_popup_seen', 'true');
    
    // Close popup after showing success
    setTimeout(() => {
      setIsOpen(false);
    }, 3000);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            className="fixed inset-0 bg-dark/60 backdrop-blur-sm z-50"
          />
          
          {/* Popup */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            data-testid="email-popup"
            className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-md mx-4"
          >
            <div className="bg-chalk relative overflow-hidden">
              {/* Close Button */}
              <button
                onClick={handleClose}
                data-testid="close-email-popup"
                className="absolute top-4 right-4 text-dark-secondary hover:text-dark transition-colors z-10"
                aria-label="Close popup"
              >
                <X size={24} />
              </button>

              {/* Decorative Top Bar */}
              <div className="h-2 bg-gradient-to-r from-amaranth via-thulian to-brook" />

              <div className="p-8 lg:p-10">
                {!isSubmitted ? (
                  <>
                    {/* Icon */}
                    <div className="w-16 h-16 mx-auto mb-6 bg-amaranth/10 rounded-full flex items-center justify-center">
                      <Gift className="text-amaranth" size={32} />
                    </div>

                    {/* Headline */}
                    <h2 className="font-serif text-dark text-2xl lg:text-3xl text-center mb-3">
                      Exclusive Offer
                    </h2>
                    
                    <p className="text-center text-amaranth font-semibold text-lg mb-2">
                      Get 10% Off Your First Treatment
                    </p>
                    
                    <p className="text-center text-dark-secondary text-sm mb-8">
                      Join our goddess circle for exclusive offers, beauty tips, 
                      and early access to new treatments.
                    </p>

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <Input
                        type="email"
                        placeholder="Enter your email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        data-testid="popup-email-input"
                        className="bg-light border-dark/10 py-6 text-center"
                      />
                      
                      <Button
                        type="submit"
                        disabled={isLoading}
                        data-testid="popup-submit-btn"
                        className="w-full bg-amaranth hover:bg-amaranth/90 text-white py-6 rounded-full"
                      >
                        {isLoading ? (
                          'Sending...'
                        ) : (
                          <>
                            Claim My 10% Off
                            <ArrowRight size={18} className="ml-2" />
                          </>
                        )}
                      </Button>
                    </form>

                    <p className="text-center text-dark-secondary text-xs mt-4">
                      No spam, ever. Unsubscribe anytime.
                    </p>
                  </>
                ) : (
                  /* Success State */
                  <div className="text-center py-8">
                    <div className="w-16 h-16 mx-auto mb-6 bg-brook/20 rounded-full flex items-center justify-center">
                      <Gift className="text-brook" size={32} />
                    </div>
                    <h2 className="font-serif text-dark text-2xl mb-3">
                      Welcome, Goddess!
                    </h2>
                    <p className="text-dark-secondary">
                      Check your inbox for your <span className="text-amaranth font-semibold">10% discount code</span>.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default EmailPopup;
